<template>
  <div class="shipment-custom-create-root">
    <button v-if="canCreateShipment" id="shipmentCustomCreateTrigger" type="button"
      class="button pill -text shipment-custom-create-trigger" aria-label="Create custom shipment"
      data-tippy-content="Create custom shipment" data-tippy-placement="top" @click="openModal">
      <span class="icon-plus-21 icon-grey" aria-hidden="true"></span>
    </button>

    <Teleport to="body">
      <div v-if="isOpen" class="shipment-custom-create-backdrop" @mousedown.self="closeModal">
        <form class="shipment-custom-create-modal" @submit.prevent="submitForm">
          <header class="shipment-custom-create-header">
            <div>
              <h2>Create custom shipment</h2>
              <p>Create an active shipment and sync it with the selected courier.</p>
            </div>
            <button type="button" class="shipment-custom-create-close" aria-label="Close" @click="closeModal">
              <i class="material-icons">close</i>
            </button>
          </header>

          <div class="shipment-custom-create-body">
            <div class="shipment-custom-create-grid">
              <label class="shipment-custom-create-field">
                <span>Tracking number</span>
                <Input v-model="form.tracking" variant="form" autocomplete="off" required />
              </label>

              <label class="shipment-custom-create-field">
                <span>Courier</span>
                <Select v-model="form.courier" :options="courierOptions" aria-label="Courier" variant="form" />
              </label>

              <label class="shipment-custom-create-field">
                <span>Type</span>
                <Select v-model="form.type" :options="typeOptions" aria-label="Custom shipment type" variant="form" />
              </label>

              <label class="shipment-custom-create-field">
                <span>Direction</span>
                <Select v-model="form.direction" :options="directionOptions" aria-label="Direction" variant="form" />
              </label>

              <label class="shipment-custom-create-field">
                <span>Customer</span>
                <Input v-model="form.customer" variant="form" autocomplete="organization"
                  :disabled="form.direction === 'inbound'" />
              </label>

              <label class="shipment-custom-create-field">
                <span>Reference</span>
                <Input v-model="form.referenceNo" variant="form" autocomplete="off" />
              </label>

              <label v-if="form.type === 'RMA'" class="shipment-custom-create-field">
                <span>RMA label expiration</span>
                <Input v-model="form.rmaExpirationDate" variant="form" type="date" required />
              </label>
            </div>

            <label class="shipment-custom-create-field shipment-custom-create-notes">
              <span>Creation note</span>
              <TextArea v-model="form.notes" variant="form" :rows="3"
                placeholder="Optional context for the activity log" />
            </label>

            <div v-if="errorMessage" class="shipment-custom-create-message error">{{ errorMessage }}</div>
            <div v-if="successMessage" class="shipment-custom-create-message success">{{ successMessage }}</div>
          </div>

          <footer class="shipment-custom-create-footer">
            <Button type="button" variant="ghost" tone="neutral" :disabled="isSubmitting"
              @click="closeModal">Cancel</Button>
            <Button type="submit" variant="solid" tone="primary" :disabled="isSubmitDisabled">
              {{ isSubmitting ? 'Creating...' : 'Create shipment' }}
            </Button>
          </footer>
        </form>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { httpsCallable } from 'firebase/functions';

import { functions } from '@/modules/firebase';
import {
  getCurrentUser,
  refreshCurrentUserProfile,
  USER_PROFILE_UPDATED_EVENT,
  type CachedUser
} from '@/modules/firebase/Auth';
import { Button, Input, Select, TextArea } from '@/components/ui';
import type { SelectOption } from '@/components/ui';

type CreateCustomShipmentResponse = {
  status: 'ok' | 'error';
  message?: string;
  tracking?: string;
};

interface CustomShipmentFormState {
  tracking: string;
  courier: string;
  type: string;
  direction: string;
  customer: string;
  referenceNo: string;
  rmaExpirationDate: string;
  notes: string;
}

interface CreateCustomShipmentPayload {
  tracking: string;
  courier: string;
  type: string;
  direction: string;
  customer?: string;
  referenceNo?: string;
  destination?: {
    city: string;
    state: string;
    country: string;
    countrycode: string;
    zip: string;
  };
  notes?: string;
  rmaExpiresAt?: number;
  rmaWindowDays?: number;
}

const INBOUND_DESTINATION = {
  city: 'Waterloo',
  state: 'ON',
  country: 'Canada',
  countrycode: 'CA',
  zip: 'N2L 0C7'
};

const courierOptions: SelectOption[] = [
  { value: 'UPS', label: 'UPS' },
  { value: 'FedEx', label: 'FedEx' },
  { value: 'USPS', label: 'USPS' },
  { value: 'DHL', label: 'DHL' },
  { value: 'CEVA', label: 'CEVA' },
  { value: 'dayross', label: 'Day & Ross' }
];

const typeOptions: SelectOption[] = [
  { value: 'RMA', label: 'RMA' },
  { value: 'CSW', label: 'CSW' },
  { value: 'Purchasing', label: 'Purchasing' },
  { value: 'R&D', label: 'R&D' },
  { value: 'Other', label: 'Other' }
];

const directionOptions: SelectOption[] = [
  { value: 'inbound', label: 'Inbound' },
  { value: 'outbound', label: 'Outbound' }
];

const createCustomShipment = httpsCallable<CreateCustomShipmentPayload, CreateCustomShipmentResponse>(
  functions,
  'createCustomShipment'
);

const isOpen = ref(false);
const isSubmitting = ref(false);
const errorMessage = ref('');
const successMessage = ref('');
const currentUser = ref<CachedUser | null>(null);
const canCreateShipment = computed(() => currentUser.value?.permissions?.shipmentCreate === true);

function handleUserProfileUpdated(event: Event): void {
  currentUser.value = (event as CustomEvent<CachedUser>).detail;
}

const form = reactive<CustomShipmentFormState>(createInitialFormState());

const isSubmitDisabled = computed(() => {
  return isSubmitting.value
    || !form.tracking.trim()
    || !form.courier
    || !form.type
    || (form.type === 'RMA' && !form.rmaExpirationDate);
});

function createInitialFormState(): CustomShipmentFormState {
  return {
    tracking: '',
    courier: 'UPS',
    type: 'RMA',
    direction: 'inbound',
    customer: 'Voltera',
    referenceNo: '',
    rmaExpirationDate: getDateInputValue(30),
    notes: ''
  };
}

function resetForm(): void {
  Object.assign(form, createInitialFormState());
}

function getDateInputValue(daysFromNow: number): string {
  const date = new Date();
  date.setDate(date.getDate() + daysFromNow);
  return date.toISOString().slice(0, 10);
}

function dateInputToUnixSeconds(dateInput: string): number | undefined {
  if (!dateInput) {
    return undefined;
  }

  return Math.floor(new Date(`${dateInput}T23:59:59`).getTime() / 1000);
}

function openModal(): void {
  errorMessage.value = '';
  successMessage.value = '';
  isOpen.value = true;
}

function closeModal(): void {
  if (isSubmitting.value) {
    return;
  }

  isOpen.value = false;
  errorMessage.value = '';
  successMessage.value = '';
}

async function submitForm(): Promise<void> {
  if (isSubmitDisabled.value) {
    return;
  }

  isSubmitting.value = true;
  errorMessage.value = '';
  successMessage.value = '';

  try {
    const isInbound = form.direction === 'inbound';
    const payload: CreateCustomShipmentPayload = {
      tracking: form.tracking.trim(),
      courier: form.courier,
      type: form.type,
      direction: form.direction,
      customer: isInbound ? 'Voltera' : form.customer.trim() || undefined,
      referenceNo: form.referenceNo.trim() || undefined,
      destination: isInbound ? INBOUND_DESTINATION : undefined,
      notes: form.notes.trim() || undefined,
      rmaExpiresAt: form.type === 'RMA' ? dateInputToUnixSeconds(form.rmaExpirationDate) : undefined,
      rmaWindowDays: 30
    };

    const response = await createCustomShipment(payload);
    const result = response.data;

    if (result.status !== 'ok') {
      errorMessage.value = result.message || 'Failed to create custom shipment';
      return;
    }

    successMessage.value = result.message || 'Custom shipment created';
    window.dispatchEvent(new CustomEvent('jidoka:custom-shipment-created', {
      detail: { tracking: result.tracking || form.tracking }
    }));
    resetForm();
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Failed to create custom shipment';
  } finally {
    isSubmitting.value = false;
  }
}

watch(() => form.type, (type) => {
  if (type === 'RMA' && !form.rmaExpirationDate) {
    form.rmaExpirationDate = getDateInputValue(30);
  }
});

watch(() => form.direction, (direction, previousDirection) => {
  if (direction === 'inbound') {
    form.customer = 'Voltera';
    return;
  }

  if (previousDirection === 'inbound' && form.customer === 'Voltera') {
    form.customer = '';
  }
});

onMounted(async () => {
  window.addEventListener(USER_PROFILE_UPDATED_EVENT, handleUserProfileUpdated);

  try {
    await refreshCurrentUserProfile();
    currentUser.value = getCurrentUser();
  } catch (error) {
    console.warn('[ShipmentCustomCreate] Unable to verify shipmentCreate permission:', error);
    currentUser.value = null;
  }
});

onBeforeUnmount(() => {
  window.removeEventListener(USER_PROFILE_UPDATED_EVENT, handleUserProfileUpdated);
});
</script>
